// Generates demo CSV content for the SaaS churn vertical
// Must produce: stated_price=35%, validated_price=12%, largest hidden driver=activation_failure

import type { CustomerRecord } from './StatedVsActualPass'

export interface DemoDataset {
  survey: string
  behavioral: string
  outcomes: string
  customers: CustomerRecord[]
}

function csvRow(values: (string | number | boolean)[]): string {
  return values.map(v => {
    const s = String(v)
    return s.includes(',') ? `"${s}"` : s
  }).join(',')
}

export function generateSaasChurnDemo(): DemoDataset {
  const TOTAL_CHURNED = 247
  // Stated reasons (must sum to ~247)
  // Price: 35% = 87, Features: 18% = 44, Support: 12% = 30, Other: 35% = 86
  const STATED = [
    { reason: 'Too expensive', count: 86 },
    { reason: 'Missing features', count: 44 },
    { reason: 'Poor support', count: 30 },
    { reason: 'No longer needed', count: 52 },
    { reason: 'Found a better alternative', count: 35 },
  ]
  const RETAINED = 800

  // Build churned customer records
  const customers: CustomerRecord[] = []

  // "Too expensive" stated — but only 12% are truly price sensitive = ~10 customers
  // Rest: activation failure (18%), engagement decay (5%)
  let cid = 1000
  for (const { reason, count } of STATED) {
    for (let i = 0; i < count; i++) {
      const id = `CUST-${cid++}`
      let core_feature_used = true
      let usage_trend: CustomerRecord['usage_trend'] = 'stable'
      let days_since_last_use = 3
      let billing_event_in_30d = false
      let team_member_left = false
      let competitor_signal = false

      if (reason === 'Too expensive') {
        // 30/86 are truly price sensitive → validated_share = 30/247 ≈ 12%
        if (i < 30) {
          // True price sensitivity: used product, stable usage, no billing event, cancelled at renewal
          core_feature_used = true
          usage_trend = 'stable'
          days_since_last_use = 4
          billing_event_in_30d = false
        } else if (i < 73) {
          // Activation failure — never used core feature
          core_feature_used = false
          usage_trend = 'declining'
          days_since_last_use = 60
        } else {
          // Engagement decay
          usage_trend = 'declining'
          days_since_last_use = 35
        }
      } else if (reason === 'Missing features') {
        if (i < 15) { competitor_signal = true }
        else { core_feature_used = false; days_since_last_use = 45 }
      } else if (reason === 'Poor support') {
        if (i < 12) { team_member_left = true }
        else { usage_trend = 'declining'; days_since_last_use = 25 }
      } else if (reason === 'No longer needed') {
        usage_trend = 'declining'
        days_since_last_use = 45
      } else {
        competitor_signal = i < 20
        if (!competitor_signal) { usage_trend = 'declining'; days_since_last_use = 20 }
      }

      customers.push({
        customer_id: id,
        stated_reason: reason,
        core_feature_used,
        usage_trend,
        days_since_last_use,
        billing_event_in_30d,
        team_member_left,
        competitor_signal,
      })
    }
  }

  // Survey CSV
  const surveyHeaders = ['customer_id', 'email', 'cancellation_reason', 'nps_score', 'comments', 'cancel_date']
  const surveyRows = [surveyHeaders.join(',')]
  const comments: Record<string, string[]> = {
    'Too expensive': [
      'The price increased without much notice',
      'I can get similar features elsewhere for less',
      'Budget cuts forced my hand',
    ],
    'Missing features': [
      'I needed Salesforce integration that isn\'t there',
      'The reporting is too limited for our needs',
      'Competitors offer better automation',
    ],
    'Poor support': [
      'Tickets took days to get a response',
      'The onboarding was confusing and no one helped',
      'Support quality declined over the past year',
    ],
    'No longer needed': [
      'We changed our process and don\'t need this anymore',
      'Team restructure — this product doesn\'t fit our new workflow',
      'We built an in-house solution',
    ],
    'Found a better alternative': [
      'Switched to a competitor with better pricing',
      'The new tool integrates better with our stack',
      'Our investors recommended a different platform',
    ],
  }

  for (const c of customers) {
    const commentList = comments[c.stated_reason] ?? ['']
    const commentIdx = Math.abs(parseInt(c.customer_id.replace('CUST-', '')) % commentList.length)
    const nps = c.usage_trend === 'declining' ? String(Math.floor(3 + (parseInt(c.customer_id.replace('CUST-', '')) % 4)))
      : c.billing_event_in_30d ? String(5 + (parseInt(c.customer_id.replace('CUST-', '')) % 3))
      : String(2 + (parseInt(c.customer_id.replace('CUST-', '')) % 5))

    surveyRows.push(csvRow([
      c.customer_id,
      `${c.customer_id.toLowerCase()}@example-churn.com`,
      c.stated_reason,
      nps,
      commentList[commentIdx],
      '2026-05-15',
    ]))
  }

  // Behavioral CSV (Mixpanel-format)
  const behavioralHeaders = ['distinct_id', 'event', 'time', 'core_feature_used', 'session_count_30d', 'usage_trend', 'days_since_last_session']
  const behavioralRows = [behavioralHeaders.join(',')]
  for (const c of customers) {
    const sessionCount = c.core_feature_used
      ? (c.usage_trend === 'declining' ? String(2 + parseInt(c.customer_id.replace('CUST-', '')) % 5)
        : String(10 + parseInt(c.customer_id.replace('CUST-', '')) % 20))
      : '0'
    behavioralRows.push(csvRow([
      c.customer_id,
      'subscription_cancelled',
      '2026-05-15T12:00:00Z',
      c.core_feature_used ? 'true' : 'false',
      sessionCount,
      c.usage_trend,
      c.days_since_last_use,
    ]))
  }
  // Add retained customers
  for (let i = 0; i < RETAINED; i++) {
    behavioralRows.push(csvRow([
      `CUST-RET-${i}`,
      'session',
      '2026-05-20T09:00:00Z',
      'true',
      String(15 + i % 30),
      'stable',
      String(1 + i % 7),
    ]))
  }

  // Outcomes CSV (Stripe-format)
  const outcomeHeaders = ['id', 'email', 'created', 'status', 'subscription_status', 'canceled_at', 'plan']
  const outcomeRows = [outcomeHeaders.join(',')]
  for (const c of customers) {
    outcomeRows.push(csvRow([
      c.customer_id,
      `${c.customer_id.toLowerCase()}@example-churn.com`,
      '2025-08-01',
      'canceled',
      'canceled',
      '2026-05-15',
      'pro_monthly',
    ]))
  }

  return {
    survey: surveyRows.join('\n'),
    behavioral: behavioralRows.join('\n'),
    outcomes: outcomeRows.join('\n'),
    customers,
  }
}
